import React, { Fragment as F } from 'react'
import get from 'lodash/get'

import { money, num, percent } from 'utils'

const getMoney = (...args) => money(get(...args))

export const FIELDS = {
  vehicleDescription: {
    name: 'Vehicle Description',
    value: (_, q) => get(q, 'mscanDescription'),
    hide: (_, q, { internal }) => !internal || !get(q, 'mscanDescription'),
  },
  msrp: {
    name: 'MSRP',
    value: (_, q) => getMoney(q, 'adjMsrp', 0),
    hide: (_, q) => get(q, 'adjMsrp', 0) === 0,
  },
  cashPrice: {
    name: 'Selling Price',
    value: p => getMoney(p, 'SellingPrice', 0),
  },
  cashMSRP: {
    name: 'MSRP',
    value: (_, q) => getMoney(q, 'adjMsrp', 0),
    hide: (_, q) => !get(q, 'adjMsrp', 0),
  },
  price: {
    name: 'Vehicle Selling Price',
    value: (_, q) => getMoney(q, 'price', 0),
  },
  priceAdjustment: {
    name: 'Price Adjustment',
    value: p => getMoney(p, 'PriceAdjustment', 0),
    hide: p => !get(p, 'PriceAdjustment', 0),
  },
  cashFeesTotal: {
    name: 'Total Fees',
    className: 'sum',
    value: p =>
      money(
        get(p, 'CustomFees', 0) +
          get(p, 'OtherFees', 0) +
          get(p, 'RegistrationFee', 0),
      ),
  },
  cashRebateTotal: {
    name: 'Total Rebates',
    value: p => getMoney(p, 'Rebate', 0),
    className: 'sum',
    hide: p => get(p, 'Rebate', 0) <= 0,
  },
  cashSalesTax: {
    name: 'Sales Tax',
    value: p => getMoney(p, 'SalesTax', 0),
  },
  cashDeficiency: {
    name: 'Remaining Inception Fees',
    value: p => getMoney(p, 'Lease.InceptionFeeDeficiency', 0),
    hide: p => get(p, 'Lease.InceptionFeeDeficiency', 0) === 0,
  },
  freight: {
    name: 'Freight Charge',
    value: (_, q) => getMoney(q, 'freight', 0),
    hide: (_, q) => !get(q, 'freight', 0),
  },
  cashSubTotal: {
    name: 'Vehicle Subtotal',
    value: (
      _,
      q,
      { rebatesTotal = 0, combinedAddsTotal = 0, discountTotal = 0 },
    ) => {
      return money(
        get(q, 'price', 0) +
          get(q, 'freight', 0) +
          combinedAddsTotal -
          discountTotal -
          rebatesTotal,
      )
    },
  },
  retailSubTotal: {
    name: 'Vehicle Subtotal',
    className: 'sum',
    value: (
      _,
      q,
      {
        rebatesTotal = 0,
        combinedAddsTotal = 0,
        discountTotal = 0,
        dealerIncentivesReducePrice,
        dealerCashTotal,
      },
    ) => {
      return money(
        get(q, 'price', 0) +
          get(q, 'freight', 0) +
          combinedAddsTotal -
          discountTotal -
          rebatesTotal -
          (dealerIncentivesReducePrice ? dealerCashTotal : 0),
      )
    },
  },
  leaseSubTotal: {
    name: 'Vehicle Subtotal',
    value: (
      p,
      q,
      {
        combinedAddsTotal,
        discountTotal,
        dealerIncentivesReducePrice,
        dealerCashTotal,
      },
    ) => {
      return money(
        get(q, 'price', 0) +
          get(q, 'freight', 0) +
          combinedAddsTotal -
          discountTotal -
          (dealerIncentivesReducePrice ? dealerCashTotal : 0) +
          get(p, 'Lease.InceptionFeeDeficiency', 0),
      )
    },
    className: 'sum',
  },
  totalCashDealRebates: {
    name: 'Rebates',
    value: p => getMoney(p, 'Rebate', 0),
  },
  totalCashDealCustomFees: {
    name: 'Total Fees',
    value: p => getMoney(p, 'CustomFees', 0),
    className: 'sum',
  },
  totalCashDealOtherFees: {
    name: 'State/City Fees',
    value: p => getMoney(p, 'OtherFees', 0),
    hide: p => !get(p, 'OtherFees', 0),
  },
  totalCashDealDue: {
    name: 'Total Cash Due',
    value: p => getMoney(p, 'MonthlyPayment', 0),
    className: 'sum',
  },
  program: {
    name: 'Lender and Program',
    value: (p, _, { program, getBankByCode }) => (
      <span>
        {getBankByCode(get(p, 'Lender', 'unknown'))} / {get(program, 'Code')}{' '}
        {get(p, 'SpecialStandard') ? '[Special]' : '[Standard]'}
      </span>
    ),
  },
  advance: {
    name: 'Amount Financed',
    value: p => getMoney(p, 'AmountFinanced', 0),
  },
  dueAtSigning: {
    name: 'Total Due at Delivery', // TODO this label is hard
    className: 'sum',
    value: p => getMoney(p, 'CustomerCash', 0),
  },
  totalRebates: {
    name: 'Total Rebates',
    className: 'sum',
    value: (p, q, { rebatesTotal }) => money(rebatesTotal),
  },
  totalDealerCash: {
    name: 'Total Dealer Cash',
    className: 'sum',
    value: (p, q, { dealerCashTotal }) => money(dealerCashTotal),
    hide: (p, q, { internal }) => !internal,
  },
  rate: {
    name: p => (get(p, 'APRLMFType') === 0 ? 'APR' : 'Money Factor'),
    type: 'number',
    value: p => {
      let rate = get(p, 'APRLMF')

      return get(p, 'APRLMFType') === 0
        ? percent(rate / 100.0)
        : parseFloat(rate)
    },
  },
  totalFees: {
    name: 'Total Fees',
    className: 'sum',
    value: (p, q, { mode }) =>
      money(
        get(q, 'fees', [])
          .filter(f => f[mode])
          .reduce((ret, f) => ret + f.value, 0),
      ),
  },
  loanTotalTax: {
    name: p => `Est. Sales Tax @ ${get(p, 'TaxRate')}%`,
    value: p => <F>{getMoney(p, 'SalesTax', 0)}</F>,
    className: 'sum',
  },
  leaseTotalTax: {
    name: p => `Est. Sales Tax @ ${get(p, 'TaxRate')}%`,
    value: p =>
      money(
        get(p, 'SalesTax', 0) +
          get(p, 'CityTax', 0) +
          get(p, 'Lease.OtherTaxInFees', 0) +
          get(p, 'Lease.OtherTaxInCap', 0) +
          get(p, 'Lease.CustomFeeTaxInCap', 0) +
          get(p, 'Lease.CustomFeeTaxInFees', 0),
      ),
    hide: p =>
      !(
        get(p, 'SalesTax', 0) +
        get(p, 'CityTax', 0) +
        get(p, 'Lease.OtherTaxInFees', 0) +
        get(p, 'Lease.OtherTaxInCap', 0) +
        get(p, 'Lease.CustomFeeTaxInCap', 0) +
        get(p, 'Lease.CustomFeeTaxInFees', 0)
      ),
  },
  leaseUseTax: {
    name: p => `Est. Monthly Use Tax @ ${get(p, 'Lease.UseTaxRate')}%`,
    value: p => getMoney(p, 'Lease.UseTax', 0),
    hide: p => !get(p, 'Lease.UseTax', 0),
  },
  salesTax: {
    name: p => `Est. State Sales Tax @ ${get(p, 'TaxRate')}%`,
    value: p =>
      money(get(p, 'SalesTax', 0) + get(p, 'Lease.OtherTaxInFees', 0)),

    hide: p => get(p, 'SalesTax', 0) + get(p, 'Lease.OtherTaxInFees', 0) === 0,
  },
  cityTax: {
    name: p => `Est. City Tax @ ${get(p, 'CityTaxRate')}%`,
    value: p => money(get(p, 'CityTax', 0)),
    hide: p => get(p, 'CityTax', 0) === 0,
  },
  leaseCustomFeeTax: {
    name: 'Est. Taxes on Fees',
    //className: 'indent',
    value: p =>
      money(
        get(p, 'Lease.CustomFeeTaxInCap', 0) +
          get(p, 'Lease.CustomFeeTaxInFees', 0),
      ),
    hide: p =>
      get(p, 'Lease.CustomFeeTaxInCap', 0) +
        get(p, 'Lease.CustomFeeTaxInFees', 0) ===
      0,
  },
  loanCustomFeeTax: {
    name: 'Includes Est. Taxes on Fees',
    value: p => getMoney(p, 'Retail.CustomFeeTax', 0),
    className: 'indent',
    hide: p => get(p, 'Retail.CustomFeeTax', 0) === 0,
  },
  downPaymentTax: {
    name: p =>
      `Est. Cap Cost Reduction Tax @ ${get(
        p,
        'Lease.CapCostReductionTaxRate',
      )}%`,
    value: p => getMoney(p, 'Lease.DownpaymentTax', 0),
    hide: p => get(p, 'Lease.DownpaymentTax', 0) === 0,
  },
  loanRebateTaxes: {
    name: 'Includes Est. Taxes on Rebates',
    value: p => getMoney(p, 'Retail.RebateTax', 0),
    className: 'indent',
    hide: p => get(p, 'Retail.RebateTax', 0) === 0,
  },
  loanTotalTaxes: {
    name: 'Estimated Taxes',
    valueOld: p =>
      money(get(p, 'SalesTax', 0) + get(p, 'Loan.CustomFeeTax', 0)),
    value: p => money(get(p, 'SalesTax', 0) + get(p, 'Loan.CustomFeeTax', 0)),
    className: 'sum',
  },
  cashTotalTaxes: {
    name: 'Estimated Fees and Taxes',
    value: p => money(get(p, 'Retail.TotalTaxesFees', 0)),
    className: 'sum',
  },
  acqFee: {
    name: 'Acquisition Fee',
    value: p => getMoney(p, 'Lease.AcqusitionFee', 0), // NOTE intentional misspelling
  },
  securityDeposit: {
    name: 'Security Deposit',
    value: p =>
      get(p, 'Lease.SecurityDepositWaived')
        ? 'Waived'
        : getMoney(p, 'Lease.SecurityDeposit', 0),
  },
  loanTotalFees: {
    name: 'Total Fees',
    className: 'sum',
    value: (p, q) =>
      money(
        parseFloat(get(p, 'WasteTireFee', 0)) +
          parseFloat(get(p, 'RegistrationFee', 0)) +
          get(q, 'fees', [])
            .filter(f => f.loan)
            .reduce((ret, f) => ret + parseFloat(f.value), 0),
      ),
  },
  term: {
    name: 'Length of Program in Months',
    value: p => get(p, 'Term', 0),
  },
  sumOfPayments: {
    name: 'Total Sum of Payments',
    value: p => {
      return money(get(p, 'MonthlyPayment', 0) * get(p, 'Term', 0))
    },
  },
  calculatedRegistrationFee: {
    name: 'State & City Fees',
    value: p => money(parseFloat(get(p, 'RegistrationFee', 0))),
    hide: p => get(p, 'RegistrationFee', 0) === 0,
  },
  cashTotalFeesAndTax: {
    name: 'Total Fees & Taxes',
    value: p => money(parseFloat(get(p, 'Retail.TotalTaxesFees', 0))),
  },
  loanTotalFeesAndTax: {
    name: 'Total Fees & Taxes',
    className: 'sum',
    value: (p, q) =>
      money(
        parseFloat(get(p, 'SalesTax', 0)) +
          parseFloat(get(p, 'CityTax', 0)) +
          //parseFloat(get(p, 'Retail.CustomFeeTax', 0)) +
          //parseFloat(get(p, 'Retail.RebateTax', 0)) +
          parseFloat(get(p, 'DocStampTax', 0)) +
          parseFloat(get(p, 'WasteTireFee', 0)) +
          parseFloat(get(p, 'InventoryTax', 0)) +
          get(p, 'SupplementalTitleFee', 0) +
          get(q, 'fees', [])
            .filter(f => f.loan)
            .reduce((ret, f) => ret + parseFloat(f.value), 0) +
          parseFloat(get(p, 'RegistrationFee', 0)),
      ),
  },
  leaseTotalFees: {
    name: 'Total Fees',
    className: 'sum',
    value: (p, q) =>
      money(
        parseFloat(get(p, 'Lease.AcqusitionFee', 0)) +
          parseFloat(get(p, 'Lease.SecurityDeposit', 0)) +
          parseFloat(get(p, 'WasteTireFee', 0)) +
          parseFloat(get(p, 'SupplementalTitleFee', 0)) +
          parseFloat(get(p, 'InventoryTax', 0)) +
          get(q, 'fees', [])
            .filter(f => f.lease)
            .reduce((ret, f) => ret + parseFloat(f.value), 0),
        parseFloat(get(p, 'RegistrationFee', 0)),
      ),
  },
  leaseTotalFeesAndTax: {
    name: 'Total Fees and Taxes',
    className: 'sum',
    value: (p, q) =>
      money(
        parseFloat(get(p, 'SalesTax', 0)) +
          parseFloat(get(p, 'Lease.DownpaymentTax', 0)) +
          parseFloat(get(p, 'Lease.OtherTaxInFees', 0)) +
          parseFloat(get(p, 'Lease.OtherTaxInCap', 0)) +
          parseFloat(get(p, 'Lease.CustomFeeTaxInCap', 0)) +
          parseFloat(get(p, 'Lease.CustomFeeTaxInFees', 0)) +
          parseFloat(get(p, 'Lease.AcqusitionFee', 0)) +
          parseFloat(get(p, 'Lease.SecurityDeposit', 0)) +
          parseFloat(get(p, 'WasteTireFee', 0)) +
          get(p, 'SupplementalTitleFee', 0) +
          get(q, 'fees', [])
            .filter(f => f.lease)
            .reduce((ret, f) => ret + parseFloat(f.value), 0),
        parseFloat(get(p, 'RegistrationFee', 0)),
      ),
  },
  cash: {
    name: 'Cash from Customer',
    value: p => getMoney(p, 'CustomerCash'),
  },
  rebateDown: {
    name: 'Cap Cost Reduction from Rebates',
    value: p => getMoney(p, 'DownpaymentFromRebate'),
  },
  equityDown: {
    name: 'Cap Cost Reduction from Equity',
    value: p => getMoney(p, 'DownpaymentFromEquity'),
  },
  cashDown: {
    name: 'Cap Cost Reduction from Cash',
    value: p => getMoney(p, 'DownpaymentFromCash'),
  },
  ccrFromCash: {
    name: 'Cap Cost Reduction from Cash',
    value: p => money(get(p, 'DownpaymentFromCash', 0)),
  },
  ccrFromEquity: {
    name: 'Cap Cost Reduction from Equity',
    value: p => money(Math.max(0, get(p, 'DownpaymentFromEquity', 0))),
  },
  totalCCR: {
    name: 'Total Cap Cost Reduction',
    value: p => getMoney(p, 'Downpayment', 0),
    className: 'sum',
  },
  residual: {
    name: p => `Residual Value @ ${get(p, 'Lease.ResidualPct', 0)}% of MSRP`,
    value: p => getMoney(p, 'Lease.ResidualValue', 0),
  },
  expectedMileage: {
    name: (_, q) =>
      `Expected Total Mileage @ ${get(q, 'miles')} miles per year`,
    value: p => num(get(p, 'Lease.MileageExpected', 0)),
  },
  tradeAllowance: {
    name: 'Trade Allowance',
    value: p => getMoney(p, 'TradeAllowance', 0),
    hide: p => get(p, 'TradeAllowance', 0) === 0,
  },
  tradePayoff: {
    name: 'Trade Payoff',
    value: p => money(-1 * get(p, 'TradePayoff', 0)),
    hide: p => get(p, 'TradePayoff', 0) === 0,
  },
  paymentsRemaining: {
    name: 'Payments Remaining',
    value: p => get(p, 'NumberOfPayments', 0),
  },
  payment: {
    name: 'Monthly Payment',
    value: p => getMoney(p, 'MonthlyPayment', 0),
  },
  preTaxPayment: {
    name: 'Est. Base Payment',
    value: p => getMoney(p, 'MonthlyPaymentBeforeTax', 0),
    hide: p =>
      getMoney(p, 'MonthlyPaymentBeforeTax', 0) ===
      getMoney(p, 'MonthlyPayment', 0),
  },
  leaseInceptionFees: {
    name: 'Total Inception Fees',
    value: p => money(get(p, 'Lease.InceptionFee', 0)),
  },
  totalCashDealEquity: {
    name: 'Total Trade Equity',
    value: p => money(get(p, 'TradeAllowance', 0) - get(p, 'TradePayoff', 0)),
  },
  totalCashDealEquitySum: {
    name: 'Total Trade Equity',
    className: 'sum',
    value: p => money(get(p, 'TradeAllowance', 0) - get(p, 'TradePayoff', 0)),
  },
  cashEquity: {
    name: 'Total Customer Equity',
    value: p =>
      money(
        get(p, 'CustomerCash') +
          get(p, 'TradeAllowance', 0) -
          get(p, 'TradePayoff', 0),
      ),
  },
  equity: {
    name: 'Total Customer Equity',
    className: 'sum',
    value: p =>
      money(
        get(p, 'CustomerCash') +
          get(p, 'TradeAllowance', 0) -
          get(p, 'TradePayoff', 0),
      ),
  },
  wasteTireFee: {
    name: 'Waste and Tire Fee',
    value: p => getMoney(p, 'WasteTireFee', 0),
    hide: p => get(p, 'WasteTireFee', 0) === 0,
  },
  registrationFees: {
    name: 'Registration Fees',
    value: p => getMoney(p, 'RegistrationFee'),
    hide: p => get(p, 'RegistrationFee', 0) <= 0,
  },
  totalAllowance: {
    name: 'Trade Allowance',
    value: p => getMoney(p, 'TradeAllowance', 0),
  },
  totalPayoff: {
    name: 'Trade Payoff',
    value: p => getMoney(p, 'TradePayOff', 0),
  },
  totalTradeEquity: {
    name: 'Trade Equity',
    className: 'sum',
    value: p => money(get(p, 'TradeAllowance', 0) - get(p, 'TradePayoff', 0)),
  },
  njSupplementalTitleFee: {
    name: 'Supplemental Title Fee',
    value: p => money(get(p, 'SupplementalTitleFee', 0)),
    hide: p => get(p, 'SupplementalTitleFee', 0) === 0,
  },
  docStampTax: {
    name: p => `Includes Doc Stamp Tax @ ${get(p, 'DocStampTaxRate')}%`,
    value: p => getMoney(p, 'DocStampTax', 0),
    hide: p => get(p, 'DocStampTax', 0) === 0,
    className: 'indent',
  },
  inventoryTax: {
    name: p => `Inventory Tax @ ${get(p, 'InventoryTaxRate')}%`,
    value: p => getMoney(p, 'InventoryTax', 0),
    hide: p => get(p, 'InventoryTax', 0) === 0,
  },
}
